import { Fragment, useState } from 'react'
import { Search, Tag } from '@mui/icons-material'
import {
  Box,
  Card,
  CardActionArea,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { OverlayState } from '@/hooks/useOverlay'
import { showInfoSnackbar } from '@/utils/snackbars'
import { Dialog, DialogContent } from '@/components/dialog'
import copyToClipboard from '@/components/document-actions-menu/copy-to-clipboard'
import { RelevantIDEntry, useRelevantIDsContext } from './RelevantIDsProvider'

const CARD_HEIGHT = 64

type RelevantIDsDialogProps = {
  overlay: OverlayState
}

// Groups listed here sort to the top, in this order; everything else follows
// alphabetically.
const GROUP_ORDER = ['Top Level', 'Fields']

function groupRank(group: string) {
  const index = GROUP_ORDER.indexOf(group)
  return index === -1 ? GROUP_ORDER.length : index
}

function groupEntries(entries: RelevantIDEntry[]) {
  const byGroup = entries.reduce<Record<string, RelevantIDEntry[]>>(
    (map, entry) => {
      ;(map[entry.group] ||= []).push(entry)
      return map
    },
    {},
  )
  return Object.entries(byGroup).sort(([a], [b]) => {
    const rankDiff = groupRank(a) - groupRank(b)
    return rankDiff !== 0 ? rankDiff : a.localeCompare(b)
  })
}

export default function RelevantIDsDialog({ overlay }: RelevantIDsDialogProps) {
  const { entries } = useRelevantIDsContext()
  const [search, setSearch] = useState('')

  const query = search.trim().toLowerCase()
  const filteredEntries = query
    ? entries.filter((entry) => entry.label.toLowerCase().includes(query))
    : entries
  const groupedEntries = groupEntries(filteredEntries)

  function handleCopy(entry: RelevantIDEntry) {
    copyToClipboard(entry.id)
    showInfoSnackbar(`Copied ${entry.label} ID to clipboard`)
  }

  return (
    <Dialog {...overlay} title="Relevant IDs" maxWidth="lg">
      <DialogContent>
        {entries.length === 0 ? (
          <Box sx={{ py: 6 }}>
            <Typography color="text.secondary" align="center">
              Nothing to copy yet. IDs will show up here as you navigate.
            </Typography>
          </Box>
        ) : (
          <>
            <Box
              sx={{
                bgcolor: 'background.paper',
                pb: 1.5,
              }}
            >
              <TextField
                autoFocus
                variant="outlined"
                size="small"
                placeholder="Search by name"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ width: '100%', maxWidth: 320 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            {groupedEntries.length === 0 ? (
              <Box sx={{ py: 4 }}>
                <Typography color="text.secondary" align="center">
                  No IDs match “{search.trim()}”.
                </Typography>
              </Box>
            ) : (
              groupedEntries.map(([group, groupEntries], index) => (
                <Fragment key={group}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    component="div"
                    sx={{ mt: index === 0 ? 0 : 1.5, mb: 0.5 }}
                  >
                    {group}
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        sm: 'repeat(2, 1fr)',
                        md: 'repeat(3, 1fr)',
                      },
                      gap: 1,
                    }}
                  >
                    {groupEntries.map((entry) => {
                      const Icon = entry.icon ?? Tag
                      const color = entry.color ?? 'primary'
                      return (
                        <Card
                          key={entry.id}
                          variant="outlined"
                          sx={{ height: CARD_HEIGHT }}
                        >
                          <CardActionArea
                            onClick={() => handleCopy(entry)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'flex-start',
                              gap: 1.5,
                              p: 0,
                              height: '100%',
                            }}
                          >
                            <Stack
                              sx={{
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                height: '100%',
                                aspectRatio: '1',
                                color: (theme) => theme.palette[color].main,
                                bgcolor: (theme) =>
                                  alpha(theme.palette[color].main, 0.12),
                              }}
                            >
                              <Icon fontSize="small" />
                            </Stack>
                            <Stack sx={{ minWidth: 0, flexGrow: 1, pr: 1.5 }}>
                              {entry.type && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {entry.type}
                                </Typography>
                              )}
                              <Typography
                                fontWeight={500}
                                fontSize="0.8125rem"
                                noWrap
                              >
                                {entry.label}
                              </Typography>
                              {entry.secondary && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {entry.secondary}
                                </Typography>
                              )}
                              <Typography
                                noWrap
                                sx={{
                                  fontFamily: 'monospace',
                                  fontSize: '0.7rem',
                                  color: 'text.disabled',
                                }}
                              >
                                {entry.id}
                              </Typography>
                            </Stack>
                          </CardActionArea>
                        </Card>
                      )
                    })}
                  </Box>
                </Fragment>
              ))
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
